/**
 * Accounts Page Component
 * 
 * Main page for managing financial accounts.
 * Provides access to account list, add/edit functionality, and exports.
 * 
 * Features:
 * - Account list with filtering and sorting
 * - Add/Edit account modals
 * - Export functionality (CSV/Excel)
 * - Net worth calculation
 * - WCAG 2.2 AA compliance
 * 
 * @see .specify/memory/constitution.md
 * @see /Reference/Reference design-review/design-principles.md
 */

import React, { useState, useEffect } from 'react';
import { AccountCategories } from '../components/accounts/AccountCategories';
import { AddAccountModal } from '../components/accounts/AddAccountModal';
import { EditAccountModal } from '../components/accounts/EditAccountModal';
import { AccountActions } from '../components/accounts/AccountActions';
import { ImportModal } from '../components/transactions/ImportModal';
import { Account, AccountType, CreateAccountPayload, AssetCategory } from '../types/account.types';
import { hybridAccountService } from '../services/hybrid-account-service';
import { calculateCurrentBalance } from '../utils/balance-calculator';
import { getCurrentUserId, supabase } from '../lib/supabase';
import type { Transaction } from '../types/transaction.types';
import { Wallet, PiggyBank, CreditCard, Building2, ArrowUp, ArrowDown, Home } from 'lucide-react';
import 'remixicon/fonts/remixicon.css';
import { PageHeader } from '../components/PageHeader';

export function AccountsPage(): React.ReactElement {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountsWithCalculatedBalances, setAccountsWithCalculatedBalances] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [preSelectedAccountType, setPreSelectedAccountType] = useState<AccountType | undefined>(undefined);
  const [importAccountId, setImportAccountId] = useState<string | undefined>(undefined);
  const [filter, setFilter] = useState<{
    type?: AccountType;
    status?: 'active' | 'closed';
    includeInNetWorth?: boolean;
    category?: AssetCategory;
  }>({});

  // Load accounts on component mount
  useEffect(() => {
    loadAccounts();
  }, [filter]);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await hybridAccountService.getAccounts({
        type: filter.type,
        status: filter.status,
        includeInNetWorth: filter.includeInNetWorth,
        category: filter.category,
      });
      
      if (response.error) {
        throw new Error(response.error.message);
      }
      
      const loadedAccounts = response.data?.accounts || [];
      setAccounts(loadedAccounts);
      
      // Calculate balances from transactions
      await calculateAccountBalances(loadedAccounts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const calculateAccountBalances = async (accountsToCalculate: Account[]) => {
    try {
      const userId = await getCurrentUserId();
      if (!userId || accountsToCalculate.length === 0) {
        setAccountsWithCalculatedBalances(accountsToCalculate);
        return;
      }

      // Fetch transactions for each account individually (one at a time)
      // This ensures we get all transactions without hitting query limits
      const accountsWithBalances = await Promise.all(
        accountsToCalculate.map(async (account) => {
          try {
            // Fetch all transactions for this account, including transfers where this account
            // is either the source or destination
            // Note: Supabase default limit is 1000, which should be sufficient for most accounts
            const { data: regularTransactions, error: regularError } = await supabase
              .from('transactions')
              .select('*', { count: 'exact' })
              .eq('user_id', userId)
              .eq('account_id', account.id)
              .is('archived_at', null)
              .order('date', { ascending: true })
              .limit(10000); // High limit to get all transactions

            if (regularError) {
              console.error(`Error fetching regular transactions for account ${account.name}:`, regularError);
            }

            // Fetch transfer transactions where this account is the source or destination
            const { data: transferTransactions, error: transferError } = await supabase
              .from('transactions')
              .select('*', { count: 'exact' })
              .eq('user_id', userId)
              .eq('type', 'transfer')
              .is('archived_at', null)
              .or(`source_account_id.eq.${account.id},destination_account_id.eq.${account.id}`)
              .order('date', { ascending: true })
              .limit(10000); // High limit to get all transactions

            if (transferError) {
              console.error(`Error fetching transfer transactions for account ${account.name}:`, transferError);
            }

            // Convert database rows to Transaction objects
            const convertDbRowToTransaction = (row: any): Transaction => ({
              id: row.id,
              userId: row.user_id,
              accountId: row.account_id,
              categoryId: row.category_id || undefined,
              amount: parseFloat(row.amount) || 0,
              description: row.description || '',
              merchantName: row.merchant_name || undefined,
              date: row.date,
              type: row.type,
              status: row.status || 'pending',
              notes: row.notes || undefined,
              referenceNumber: row.reference_number || undefined,
              payee: row.payee || undefined,
              balanceAfter: row.balance_after ? parseFloat(row.balance_after) : undefined,
              sourceAccountId: row.source_account_id || undefined,
              destinationAccountId: row.destination_account_id || undefined,
              externalId: row.external_id || undefined,
              originalDescription: row.original_description || undefined,
              chequeNumber: row.cheque_number || undefined,
              autoCategorized: row.auto_categorized || false,
              confidenceScore: row.confidence_score ? parseFloat(row.confidence_score) : undefined,
              flaggedForReview: row.flagged_for_review || false,
              statusReason: row.status_reason || undefined,
              reviewedBy: row.reviewed_by || undefined,
              reviewedAt: row.reviewed_at || undefined,
              archivedAt: row.archived_at || undefined,
              createdAt: row.created_at,
              updatedAt: row.updated_at
            });

            // Combine regular and transfer transactions, removing duplicates
            const regularTxns = (regularTransactions || []).map(convertDbRowToTransaction);
            const transferTxns = (transferTransactions || []).map(convertDbRowToTransaction);
            
            // Create a Set of transaction IDs to avoid duplicates
            const transactionIds = new Set<string>();
            const allTransactions: Transaction[] = [];
            
            // Add regular transactions
            regularTxns.forEach(txn => {
              if (!transactionIds.has(txn.id)) {
                transactionIds.add(txn.id);
                allTransactions.push(txn);
              }
            });
            
            // Add transfer transactions (they might overlap with regular if account_id matches)
            transferTxns.forEach(txn => {
              if (!transactionIds.has(txn.id)) {
                transactionIds.add(txn.id);
                allTransactions.push(txn);
              }
            });
            
            // Sort by date
            allTransactions.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
            
            // Calculate current balance from opening balance + transactions
            const calculatedBalance = calculateCurrentBalance(
              account.openingBalance || 0,
              account.openingDate || account.createdAt,
              allTransactions,
              account.type,
              account.id
            );

            // Return account with calculated balance
            return {
              ...account,
              currentBalance: calculatedBalance,
              // Store transactions for monthly calculations
              _transactions: allTransactions,
            };
          } catch (err) {
            console.error(`Error processing account ${account.name}:`, err);
            // Return account with stored balance on error
            return {
              ...account,
              currentBalance: account.currentBalance || account.openingBalance || 0,
              _transactions: [],
            };
          }
        })
      );
      setAccountsWithCalculatedBalances(accountsWithBalances);
    } catch (err) {
      console.error('Error calculating account balances:', err);
      // Fallback to using stored balances if calculation fails
      setAccountsWithCalculatedBalances(accountsToCalculate);
    }
  };

  const handleAddAccount = async (accountData: CreateAccountPayload) => {
    try {
      setError(null);
      const response = await hybridAccountService.createAccount(accountData);
      
      if (response.error) {
        throw new Error(response.error.message);
      }
      
      const newAccount = response.data!;
      const updatedAccounts = [...accounts, newAccount];
      setAccounts(updatedAccounts);
      // Recalculate balances to include new account
      await calculateAccountBalances(updatedAccounts);
      setShowAddModal(false);
      setPreSelectedAccountType(undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create account');
    }
  };

  const handleEditAccount = async (accountData: Partial<Account>) => {
    if (!selectedAccount) return;
    
    try {
      const response = await hybridAccountService.updateAccount(selectedAccount.id, accountData);
      
      if (response.error) {
        throw new Error(response.error.message);
      }
      
      const updatedAccount = response.data!;
      const updatedAccounts = accounts.map(acc => acc.id === updatedAccount.id ? updatedAccount : acc);
      setAccounts(updatedAccounts);
      // Recalculate balances after update
      await calculateAccountBalances(updatedAccounts);
      setShowEditModal(false);
      setSelectedAccount(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update account');
    }
  };

  const handleDeleteAccount = async (accountId: string) => {
    try {
      const response = await hybridAccountService.deleteAccount(accountId);
      
      if (response.error) {
        throw new Error(response.error.message);
      }
      
      setAccounts(prev => prev.filter(acc => acc.id !== accountId));
      setAccountsWithCalculatedBalances(prev => prev.filter(acc => acc.id !== accountId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete account');
    }
  };

  const handleCloseAccount = async (
    accountId: string,
    closureDate: string,
    closureReason?: string,
    options?: { includeInNetWorth?: boolean; currentValue?: number; notesAppend?: string }
  ) => {
    try {
      const account = accounts.find(acc => acc.id === accountId);
      const isAsset = account?.type === AccountType.ASSETS;

      // Assets marked sold: exclude from net worth and zero value
      const closePayload = {
        closureDate,
        closureReason,
        includeInNetWorth: options?.includeInNetWorth ?? (isAsset ? false : undefined),
        currentValue: options?.currentValue ?? (isAsset ? 0 : undefined),
      };

      // If notes need updating for sold assets, update first (or combine after close)
      if (options?.notesAppend && account) {
        const notes = [account.notes, options.notesAppend].filter(Boolean).join('\n');
        await hybridAccountService.updateAccount(accountId, { notes });
      }

      const response = await hybridAccountService.closeAccount(accountId, closePayload);
      
      if (response.error) {
        throw new Error(response.error.message);
      }
      
      const updatedAccount = response.data!;
      // Ensure sold asset flags are applied (in case close API didn't set them)
      if (isAsset && (
        updatedAccount.includeInNetWorth !== false ||
        updatedAccount.currentValue !== 0
      )) {
        const fullUpdate = await hybridAccountService.updateAccount(accountId, {
          includeInNetWorth: false,
          currentValue: 0,
          isActive: false,
          closureDate,
          closureReason: closureReason || 'Sold',
        });
        if (fullUpdate.data) {
          const list = accounts.map(acc => acc.id === fullUpdate.data!.id ? fullUpdate.data! : acc);
          setAccounts(list);
          await calculateAccountBalances(list);
          return;
        }
      }

      const updatedAccounts = accounts.map(acc => acc.id === updatedAccount.id ? updatedAccount : acc);
      setAccounts(updatedAccounts);
      await calculateAccountBalances(updatedAccounts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close account');
    }
  };

  const handleMarkAssetAsSold = async (
    accountId: string,
    payload: {
      closureDate: string;
      closureReason?: string;
      includeInNetWorth?: boolean;
      currentValue?: number;
      notesAppend?: string;
    }
  ) => {
    await handleCloseAccount(accountId, payload.closureDate, payload.closureReason, {
      includeInNetWorth: payload.includeInNetWorth ?? false,
      currentValue: payload.currentValue ?? 0,
      notesAppend: payload.notesAppend,
    });
    setShowEditModal(false);
    setSelectedAccount(null);
  };

  const handleExportCSV = async () => {
    try {
      await hybridAccountService.exportAccountsCSV();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export CSV');
    }
  };

  const openEditModal = (account: Account) => {
    setSelectedAccount(account);
    setShowEditModal(true);
  };

  // Use calculated balances for all calculations
  const accountsForCalculations = accountsWithCalculatedBalances.length > 0 
    ? accountsWithCalculatedBalances 
    : accounts;

  // Calculate net worth (only accounts with includeInNetWorth: true)
  // Includes account balances and asset values
  const accountBalance = accountsForCalculations
    .filter(acc => acc.includeInNetWorth && acc.type !== AccountType.ASSETS)
    .reduce((sum, acc) => {
      const balance = acc.currentBalance || 0;
      return sum + (isNaN(balance) ? 0 : balance);
    }, 0);
  
  const assetValue = accountsForCalculations
    .filter(acc => acc.type === AccountType.ASSETS && acc.includeInNetWorth)
    .reduce((sum, acc) => {
      const value = acc.currentValue || 0;
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
  
  const netWorth = accountBalance + assetValue;

  // Calculate balances by account type (only accounts with includeInNetWorth: true)
  const accountTypeBalances = {
    everyday: accountsForCalculations
      .filter(acc => acc.type === AccountType.BANK_CASH && acc.includeInNetWorth)
      .reduce((sum, acc) => {
        const balance = acc.currentBalance || 0;
        return sum + (isNaN(balance) ? 0 : balance);
      }, 0),
    liabilities: accountsForCalculations
      .filter(acc => (acc.type === AccountType.CREDIT_CARD || acc.type === AccountType.LOAN) && acc.includeInNetWorth)
      .reduce((sum, acc) => {
        const balance = acc.currentBalance || 0;
        return sum + (isNaN(balance) ? 0 : balance);
      }, 0),
    savings: accountsForCalculations
      .filter(acc => acc.type === AccountType.SAVINGS && acc.includeInNetWorth)
      .reduce((sum, acc) => {
        const balance = acc.currentBalance || 0;
        return sum + (isNaN(balance) ? 0 : balance);
      }, 0),
    investments: accountsForCalculations
      .filter(acc => acc.type === AccountType.INVESTMENTS_SUPERANNUATION && acc.includeInNetWorth)
      .reduce((sum, acc) => {
        const balance = acc.currentBalance || 0;
        return sum + (isNaN(balance) ? 0 : balance);
      }, 0),
    assets: accountsForCalculations
      .filter(acc => acc.type === AccountType.ASSETS && acc.includeInNetWorth)
      .reduce((sum, acc) => {
        const value = acc.currentValue || 0;
        return sum + (isNaN(value) ? 0 : value);
      }, 0),
  };

  // Calculate asset trend indicator (aggregate: sum current values vs sum purchase prices)
  const assetAccounts = accountsForCalculations.filter(acc => acc.type === AccountType.ASSETS && acc.includeInNetWorth);
  const totalCurrentValue = assetAccounts.reduce((sum, acc) => sum + (acc.currentValue || 0), 0);
  const totalPurchasePrice = assetAccounts.reduce((sum, acc) => sum + (acc.purchasePrice || 0), 0);
  const assetTrend = totalPurchasePrice > 0 ? (totalCurrentValue > totalPurchasePrice ? 'up' : totalCurrentValue < totalPurchasePrice ? 'down' : 'neutral') : 'neutral';

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Accounts"
        description="Manage your financial accounts and track your net worth"
        helpSlug="accounts"
      />

      {/* Account Type Summary Cards */}
      <div>
        <div className="main-page-grid-4">
            {/* Total Net Worth */}
            <div className="bg-gray-100 dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Net Worth</p>
                <p className={`text-2xl font-bold ${netWorth >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  ${netWorth.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <p>Everyday + Savings + Investments + Assets - Liabilities</p>
                </div>
              </div>
            </div>

            {/* Everyday Accounts */}
            <button
              onClick={() => {
                // Expand category if needed by clicking the category header button
                const categoryButton = document.querySelector('#category-everyday button') as HTMLElement;
                if (categoryButton) {
                  // Check if category content exists (expanded) - if not, expand it
                  const categoryContent = document.querySelector('#category-everyday .px-6.pb-6');
                  if (!categoryContent) {
                    categoryButton.click();
                  }
                }
                // Small delay to allow expansion animation
                setTimeout(() => {
                  const element = document.getElementById('category-everyday');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 150);
              }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-left hover:shadow-md transition-all duration-200 cursor-pointer"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <Wallet className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Everyday</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Bank/Cash accounts</p>
                  </div>
                </div>
                <ArrowUp className="h-5 w-5 text-green-500" />
              </div>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                ${accountTypeBalances.everyday.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </button>

            {/* Liabilities */}
            <button
              onClick={() => {
                // Expand category if needed by clicking the category header button
                const categoryButton = document.querySelector('#category-liabilities button') as HTMLElement;
                if (categoryButton) {
                  // Check if category content exists (expanded) - if not, expand it
                  const categoryContent = document.querySelector('#category-liabilities .px-6.pb-6');
                  if (!categoryContent) {
                    categoryButton.click();
                  }
                }
                // Small delay to allow expansion animation
                setTimeout(() => {
                  const element = document.getElementById('category-liabilities');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 150);
              }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-left hover:shadow-md transition-all duration-200 cursor-pointer"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Liabilities</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Credit cards & loans</p>
                  </div>
                </div>
                <ArrowDown className="h-5 w-5 text-red-500" />
              </div>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                ${accountTypeBalances.liabilities.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </button>

            {/* Savings */}
            <button
              onClick={() => {
                // Expand category if needed by clicking the category header button
                const categoryButton = document.querySelector('#category-savings button') as HTMLElement;
                if (categoryButton) {
                  // Check if category content exists (expanded) - if not, expand it
                  const categoryContent = document.querySelector('#category-savings .px-6.pb-6');
                  if (!categoryContent) {
                    categoryButton.click();
                  }
                }
                // Small delay to allow expansion animation
                setTimeout(() => {
                  const element = document.getElementById('category-savings');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 150);
              }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-left hover:shadow-md transition-all duration-200 cursor-pointer"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                    <PiggyBank className="h-5 w-5 text-green-700 dark:text-green-300" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Savings</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Savings accounts</p>
                  </div>
                </div>
                <ArrowUp className="h-5 w-5 text-green-500" />
              </div>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                ${accountTypeBalances.savings.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </button>

            {/* Investments */}
            <button
              onClick={() => {
                // Expand category if needed by clicking the category header button
                const categoryButton = document.querySelector('#category-investments button') as HTMLElement;
                if (categoryButton) {
                  // Check if category content exists (expanded) - if not, expand it
                  const categoryContent = document.querySelector('#category-investments .px-6.pb-6');
                  if (!categoryContent) {
                    categoryButton.click();
                  }
                }
                // Small delay to allow expansion animation
                setTimeout(() => {
                  const element = document.getElementById('category-investments');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 150);
              }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-left hover:shadow-md transition-all duration-200 cursor-pointer"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Investments</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Super & investments</p>
                  </div>
                </div>
                <ArrowUp className="h-5 w-5 text-green-500" />
              </div>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                ${accountTypeBalances.investments.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </button>

            {/* Assets */}
            <button
              onClick={() => {
                // Expand category if needed by clicking the category header button
                const categoryButton = document.querySelector('#category-assets button') as HTMLElement;
                if (categoryButton) {
                  // Check if category content exists (expanded) - if not, expand it
                  const categoryContent = document.querySelector('#category-assets .px-6.pb-6');
                  if (!categoryContent) {
                    categoryButton.click();
                  }
                }
                // Small delay to allow expansion animation
                setTimeout(() => {
                  const element = document.getElementById('category-assets');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 150);
              }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-left hover:shadow-md transition-all duration-200 cursor-pointer"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                    <Home className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Assets</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Property, vehicles, valuables</p>
                  </div>
                </div>
                {assetTrend === 'up' && <ArrowUp className="h-5 w-5 text-green-500" />}
                {assetTrend === 'down' && <ArrowDown className="h-5 w-5 text-red-500" />}
                {assetTrend === 'neutral' && <div className="h-5 w-5" />}
              </div>
              <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                ${accountTypeBalances.assets.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </button>

            {/* Add Account Button */}
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-left hover:shadow-md transition-all duration-200 cursor-pointer flex flex-col items-center justify-center border-dashed border-2"
              aria-label="Add new account"
            >
              <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-add-line text-lg text-primary-600 dark:text-primary-400"></i>
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Add Account</span>
            </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4" role="alert">
          <div className="flex items-center">
              <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="ml-3 text-red-800 dark:text-red-200 font-medium">{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                aria-label="Dismiss error"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
          </div>
        </div>
      )}

      {/* Account Actions */}
      <div>
        <AccountActions
          onExportCSV={handleExportCSV}
          onFilterChange={setFilter}
          currentFilter={filter}
        />
      </div>

      {/* Account Categories */}
      <AccountCategories
        accounts={accountsForCalculations}
        loading={loading}
        onEdit={openEditModal}
        onDelete={handleDeleteAccount}
        onClose={handleCloseAccount}
        onAddAccount={(type) => {
          setPreSelectedAccountType(type);
          setShowAddModal(true);
        }}
        onImport={(accountId) => {
          setImportAccountId(accountId);
          setShowImportModal(true);
        }}
      />

      {/* Add Account Modal */}
      {showAddModal && (
        <AddAccountModal
          onClose={() => {
            setShowAddModal(false);
            setPreSelectedAccountType(undefined);
            setError(null);
          }}
          onSubmit={handleAddAccount}
          initialType={preSelectedAccountType}
          error={error}
        />
      )}

      {/* Edit Account Modal */}
      {showEditModal && selectedAccount && (
        <EditAccountModal
          account={selectedAccount}
          onClose={() => {
            setShowEditModal(false);
            setSelectedAccount(null);
          }}
          onSubmit={handleEditAccount}
          onDelete={() => {
            if (selectedAccount) {
              handleDeleteAccount(selectedAccount.id);
              setShowEditModal(false);
              setSelectedAccount(null);
            }
          }}
          onMarkAsSold={(payload) => {
            if (selectedAccount) {
              handleMarkAssetAsSold(selectedAccount.id, payload);
            }
          }}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          isOpen={showImportModal}
          onClose={() => {
            setShowImportModal(false);
            setImportAccountId(undefined);
          }}
          accountId={importAccountId}
          accountDisabled={!!importAccountId} // Disable account selection when opened from account card
          onImportComplete={() => {
            // Reload accounts to update balances
            loadAccounts();
          }}
        />
      )}
    </div>
  );
}
